import { getAuthHeaders } from './auth-headers';
import { LockError } from './lock-error';

const AGENT_URL = process.env.REACT_APP_PRD_AGENT_URL;

// TEMP DIAGNOSTIC prefix — remove once the streaming issue is resolved.
const DBG = '[prd-agent]';

/**
 * Sends a message to the PRD agent and processes the SSE stream.
 * @param {string} message - User message
 * @param {string} sessionId - Conversation session ID
 * @param {object} callbacks - { onChunk, onArtifact, onDone, onError }
 */
export async function sendMessage(message, sessionId, callbacks) {
    // The invoke-agent Function URL now requires a verified Bearer token
    // (backend withAuth guard). getAuthHeaders() also sets Content-Type.
    const headers = await getAuthHeaders();

    // TEMP DIAGNOSTIC: what are we sending? (mask the token — show only presence + length)
    const authVal = headers.Authorization || headers.authorization;
    console.log(DBG, 'POST', AGENT_URL, {
        hasAuthorization: Boolean(authVal),
        authPreview: authVal ? `Bearer …(${authVal.length} chars)` : null,
        xUserId: headers['X-User-Id'],
        sessionId,
        messageLen: message ? message.length : 0,
    });

    let response;
    try {
        response = await fetch(AGENT_URL, {
            method: 'POST',
            headers,
            body: JSON.stringify({ message, sessionId }),
        });
    } catch (e) {
        console.error(DBG, 'fetch threw (network/CORS)', e);
        callbacks.onError(`Network error: ${e.message}`);
        return;
    }

    // TEMP DIAGNOSTIC: response status + CORS/type headers
    console.log(DBG, 'response', {
        status: response.status,
        ok: response.ok,
        type: response.type,
        contentType: response.headers.get('content-type'),
        acao: response.headers.get('access-control-allow-origin'),
        hasBody: Boolean(response.body),
    });

    if (!response.ok) {
        const raw = await response.text();
        console.error(DBG, 'non-ok response body', raw);

        // A locked conversation answers 409 { error, code, reason } BEFORE the
        // SSE 200 header. Parse it and hand onError a LockError so the hook can
        // branch on `code` instead of the text. Defensive: an older backend
        // that sends 409 without `code` (or non-JSON) falls back to the plain
        // string, preserving prior behavior.
        if (response.status === 409) {
            try {
                const body = JSON.parse(raw);
                if (body && body.code) {
                    callbacks.onError(
                        new LockError({
                            status: 409,
                            code: body.code,
                            reason: body.reason || body.error || null,
                        }),
                    );
                    return;
                }
            } catch (_) {
                // Not JSON — fall through to the generic string error.
            }
        }

        callbacks.onError(`HTTP ${response.status}: ${raw}`);
        return;
    }

    if (!response.body) {
        console.error(DBG, 'response.body is null — cannot stream');
        callbacks.onError('No response body to stream');
        return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let chunkCount = 0;
    let sawDone = false;

    while (true) {
        const { done, value } = await reader.read();
        if (done) {
            console.log(DBG, 'reader done', { chunkCount, sawDone });
            break;
        }

        chunkCount += 1;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
            if (!line.startsWith('data: ')) continue;

            const data = line.slice(6);

            if (data === '[DONE]') {
                console.log(DBG, 'received [DONE]');
                sawDone = true;
                callbacks.onDone();
                return;
            }

            try {
                const parsed = JSON.parse(data);
                if (parsed.error) {
                    console.error(DBG, 'stream error event', parsed.error);
                    callbacks.onError(parsed.error);
                    return;
                }

                if (parsed.type === 'artifact' && parsed.artifact) {
                    console.log(DBG, 'artifact event');
                    callbacks.onArtifact(parsed.artifact);
                    continue;
                }

                // Handle title events
                if (parsed.type === 'title' && parsed.title) {
                    console.log(DBG, 'title event', parsed.title);
                    callbacks.onTitle(parsed.title);
                    continue;
                }

                callbacks.onChunk(parsed.text || data);
            } catch {
                callbacks.onChunk(data);
            }
        }
    }

    console.log(DBG, 'stream ended without [DONE], calling onDone anyway');
    callbacks.onDone();
}
